import { useEffect, useState } from 'react';
import style from './Feed.module.css';
import FeedCard from './FeedCard';
import FilterIcon from '../../../assets/svgs/Filter';
import useDroppable from '../../../hooks/useDroppable';
import {
  transformCourses,
  transformEvents,
  transformJobs,
  transformNews,
} from '../../../utils/FeedTransformData';

const API_URL = process.env.REACT_APP_API_URL || '';

const Feed = () => {
  const filters = ['All', 'Jobs', 'Courses', 'Events', 'News', 'Saved'];

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [feed, setFeed] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const droppableProps = useDroppable(feed, setFeed);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsData, coursesData, eventsData, newsData] = await Promise.all([
          fetch(`${API_URL}/feed/jobs`).then((res) => res.json()),
          fetch(`${API_URL}/feed/courses`).then((res) => res.json()),
          fetch(`${API_URL}/feed/events`).then((res) => res.json()),
          fetch(`${API_URL}/feed/news`).then((res) => res.json()),
        ]);

        const transformedJobs = transformJobs(jobsData);
        const transformedCourses = transformCourses(coursesData);
        const transformedEvents = transformEvents(eventsData);
        const transformedNews = transformNews(newsData);

        setFeed([
          ...transformedJobs,
          ...transformedCourses,
          ...transformedEvents,
          ...transformedNews,
        ]);
      } catch (error) {
        console.error('Error fetching or transforming data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedFilters.length === 0) {
      setSelectedFilters(['All']);
    }
  }, [selectedFilters, feed]);

  const handleSelect = (filter) => {
    if (filter === 'All') {
      setSelectedFilters([]);
    } else {
      const index = selectedFilters.indexOf(filter);
      if (index === -1) {
        if (selectedFilters.includes('All')) {
          setSelectedFilters([filter]);
        } else {
          setSelectedFilters([...selectedFilters, filter]);
        }
      } else {
        setSelectedFilters(selectedFilters.filter((f) => f !== filter));
      }
    }
  };

  const isFiltered = (category) =>
    selectedFilters.length === 0 ||
    selectedFilters.includes('All') ||
    selectedFilters.includes(category);

  const getSortKey = (item) => {
    const key =
      item?.title ??
      item?.name ??
      item?.jobTitle ??
      item?.courseTitle ??
      item?.eventTitle ??
      item?.newsTitle ??
      '';
    return String(key).toLowerCase();
  };

  const isAllActive =
    selectedFilters.length === 0 || selectedFilters.includes('All');

  const allSorted = [...feed].sort((a, b) => {
    const ak = getSortKey(a);
    const bk = getSortKey(b);
    if (ak && bk) return ak.localeCompare(bk);
    if (ak) return -1;
    if (bk) return 1;
    return 0;
  });

  // --- date grouping helpers for Jobs only ---
  const isToday = (d) => {
    const someDate = d instanceof Date ? d : new Date(d);
    const today = new Date();
    return (
      someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear()
    );
  };

  const isYesterday = (d) => {
    const someDate = d instanceof Date ? d : new Date(d);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      someDate.getDate() === yesterday.getDate() &&
      someDate.getMonth() === yesterday.getMonth() &&
      someDate.getFullYear() === yesterday.getFullYear()
    );
  };

  const isThisWeek = (d) => {
    const someDate = d instanceof Date ? d : new Date(d);
    const today = new Date();
    const firstDayOfWeek = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - today.getDay()
    );
    const lastDayOfWeek = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + (6 - today.getDay())
    );
    const isBeforeWeek = someDate < firstDayOfWeek;
    const isAfterWeek = someDate > lastDayOfWeek;
    const isTodayOrYesterday = isToday(someDate) || isYesterday(someDate);
    return !isTodayOrYesterday && !isBeforeWeek && !isAfterWeek;
  };

  const isThisMonth = (d) => {
    const someDate = d instanceof Date ? d : new Date(d);
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const isBeforeMonth = someDate < firstDayOfMonth;
    const isAfterMonth = someDate > lastDayOfMonth;
    const isWithinWeek = isToday(someDate) || isYesterday(someDate) || isThisWeek(someDate);
    return !isWithinWeek && !isBeforeMonth && !isAfterMonth;
  };

  const isEarlierThisYear = (d) => {
    const someDate = d instanceof Date ? d : new Date(d);
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const lastDayOfYear = new Date(today.getFullYear(), 11, 31);
    return someDate >= firstDayOfYear && someDate <= lastDayOfYear && !isThisMonth(someDate);
  };

  const showJobGroups =
    !isAllActive && (!selectedFilters.includes('Courses') || selectedFilters.includes('Jobs'));

  return (
    <div className={style.container}>
      <p className={style.title}>Feed</p>
      <div className={style.filterContainer}>
        {filters.map((filter, index) => (
          <div
            className={`${style.filter} ${selectedFilters.includes(filter) ? style.selected : ''}`}
            key={index}
            onClick={() => (filter === 'All' ? handleSelect('All') : handleSelect(filter))}
          >
            {filter}
          </div>
        ))}
      </div>

      <div onClick={() => setOpenFilter(!openFilter)} className={style.mobileFilters}>
        {openFilter ? '' : <p className={style.filterTitle}>Filter</p>}
        {selectedFilters.length > 0 && !selectedFilters.includes('All') && (
          <div className={style.filterBubble}>{selectedFilters.length}</div>
        )}
        <FilterIcon />
      </div>

      {openFilter && (
        <div className={style.filterContainerMobile}>
          {filters.map((filter, index) => (
            <div
              className={`${style.filter} ${selectedFilters.includes(filter) ? style.selected : ''}`}
              key={index}
              onClick={() => (filter === 'All' ? handleSelect('All') : handleSelect(filter))}
            >
              {filter}
            </div>
          ))}
        </div>
      )}

      <div className={style.cardsContainer}>
        {isAllActive ? (
          <div className={style.filteredFeed}>
            <p className={style.timetext}>All:</p>
            {allSorted.map((feedItem, i) => {
              const originalIndex = feed.indexOf(feedItem);
              return (
                <div
                  key={i}
                  onDrop={(e) => droppableProps.onDrop(e, originalIndex)}
                  onDragOver={droppableProps.onDragOver}
                >
                  <FeedCard feedItem={feedItem} index={originalIndex} />
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {showJobGroups && (
              <>
                {feed.some(
                  (item) => item.category === 'Jobs' && isToday(item.datePosted || new Date())
                ) &&
                  isFiltered('Jobs') && (
                    <div className={style.filteredFeed}>
                      <p className={style.timetext}>Today:</p>
                      {feed
                        .filter((item) => item.category === 'Jobs' && isToday(item.datePosted || new Date()))
                        .map((feedItem, index) =>
                          isFiltered(feedItem.category) && (
                            <div
                              key={index}
                              onDrop={(e) => droppableProps.onDrop(e, index)}
                              onDragOver={droppableProps.onDragOver}
                            >
                              <FeedCard feedItem={feedItem} index={index} />
                            </div>
                          )
                        )}
                    </div>
                  )}

                {feed.some(
                  (item) => item.category === 'Jobs' && isYesterday(item.datePosted || new Date())
                ) &&
                  isFiltered('Jobs') && (
                    <div className={style.filteredFeed}>
                      <p className={style.timetext}>Yesterday:</p>
                      {feed
                        .filter(
                          (item) => item.category === 'Jobs' && isYesterday(item.datePosted || new Date())
                        )
                        .map((feedItem, index) =>
                          isFiltered(feedItem.category) && (
                            <div
                              key={index}
                              onDrop={(e) => droppableProps.onDrop(e, index)}
                              onDragOver={droppableProps.onDragOver}
                            >
                              <FeedCard feedItem={feedItem} index={index} />
                            </div>
                          )
                        )}
                    </div>
                  )}

                {feed.some(
                  (item) => item.category === 'Jobs' && isThisWeek(item.datePosted || new Date())
                ) &&
                  isFiltered('Jobs') && (
                    <div className={style.filteredFeed}>
                      <p className={style.timetext}>This Week:</p>
                      {feed
                        .filter(
                          (item) => item.category === 'Jobs' && isThisWeek(item.datePosted || new Date())
                        )
                        .map((feedItem, index) =>
                          isFiltered(feedItem.category) && (
                            <div
                              key={index}
                              onDrop={(e) => droppableProps.onDrop(e, index)}
                              onDragOver={droppableProps.onDragOver}
                            >
                              <FeedCard feedItem={feedItem} index={index} />
                            </div>
                          )
                        )}
                    </div>
                  )}

                {feed.some(
                  (item) => item.category === 'Jobs' && isThisMonth(item.datePosted || new Date())
                ) &&
                  isFiltered('Jobs') && (
                    <div className={style.filteredFeed}>
                      <p className={style.timetext}>This Month:</p>
                      {feed
                        .filter(
                          (item) => item.category === 'Jobs' && isThisMonth(item.datePosted || new Date())
                        )
                        .map((feedItem, index) =>
                          isFiltered(feedItem.category) && (
                            <div
                              key={index}
                              onDrop={(e) => droppableProps.onDrop(e, index)}
                              onDragOver={droppableProps.onDragOver}
                            >
                              <FeedCard feedItem={feedItem} index={index} />
                            </div>
                          )
                        )}
                    </div>
                  )}

                {feed.some(
                  (item) => item.category === 'Jobs' && isEarlierThisYear(item.datePosted || new Date())
                ) &&
                  isFiltered('Jobs') && (
                    <div className={style.filteredFeed}>
                      <p className={style.timetext}>Earlier This Year:</p>
                      {feed
                        .filter(
                          (item) =>
                            item.category === 'Jobs' && isEarlierThisYear(item.datePosted || new Date())
                        )
                        .map((feedItem, index) =>
                          isFiltered(feedItem.category) && (
                            <div
                              key={index}
                              onDrop={(e) => droppableProps.onDrop(e, index)}
                              onDragOver={droppableProps.onDragOver}
                            >
                              <FeedCard feedItem={feedItem} index={index} />
                            </div>
                          )
                        )}
                    </div>
                  )}
              </>
            )}

            {feed.some((item) => item.category === 'Courses') && isFiltered('Courses') && (
              <div className={style.filteredFeed}>
                <p className={style.timetext}>Courses:</p>
                {feed
                  .filter((item) => item.category === 'Courses')
                  .map((feedItem, index) =>
                    isFiltered(feedItem.category) && (
                      <div
                        key={index}
                        onDrop={(e) => droppableProps.onDrop(e, index)}
                        onDragOver={droppableProps.onDragOver}
                      >
                        <FeedCard feedItem={feedItem} index={index} />
                      </div>
                    )
                  )}
              </div>
            )}

            {feed.some((item) => item.category === 'Events') && isFiltered('Events') && (
              <div className={style.filteredFeed}>
                <p className={style.timetext}>Events:</p>
                {feed
                  .filter((item) => item.category === 'Events')
                  .map((feedItem, index) =>
                    isFiltered(feedItem.category) && (
                      <div
                        key={index}
                        onDrop={(e) => droppableProps.onDrop(e, index)}
                        onDragOver={droppableProps.onDragOver}
                      >
                        <FeedCard feedItem={feedItem} index={index} />
                      </div>
                    )
                  )}
              </div>
            )}

            {feed.some((item) => item.category === 'News') && isFiltered('News') && (
              <div className={style.filteredFeed}>
                <p className={style.timetext}>News:</p>
                {feed
                  .filter((item) => item.category === 'News')
                  .map((feedItem, index) =>
                    isFiltered(feedItem.category) && (
                      <div
                        key={index}
                        onDrop={(e) => droppableProps.onDrop(e, index)}
                        onDragOver={droppableProps.onDragOver}
                      >
                        <FeedCard feedItem={feedItem} index={index} />
                      </div>
                    )
                  )}
              </div>
            )}
          </>
        )}
      </div>

      <p className={style.scrollText}>Scroll to see more</p>
    </div>
  );
};

export default Feed;
